import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listVersions, getVersionDetail, restoreVersion } from '@/services/flavor_versions_api';
import { currentFormulationQueryKey } from '@/hooks/useCurrentFormulation';

const versionsKey = (flavorId: string, formulaId: number) =>
  ['flavorVersion', flavorId, formulaId, 'list'] as const;

const versionDetailKey = (flavorId: string, formulaId: number, versionId: number) =>
  ['flavorVersion', flavorId, formulaId, 'detail', versionId] as const;

// Page size sent on every infinite-query request. Used both as the explicit
// `limit` so the server doesn't fall back to its default AND as the "did we
// hit the end?" sentinel — when a page returns fewer than PAGE_LIMIT rows,
// we know there are no older versions and hide the "Load older" affordance.
const PAGE_LIMIT = 50;

export function useFlavorVersions(flavorId: string, formulaId: number) {
  return useInfiniteQuery({
    queryKey: versionsKey(flavorId, formulaId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listVersions(flavorId, formulaId, { before: pageParam, limit: PAGE_LIMIT }),
    // Return undefined when we're definitely at the end so hasNextPage flips
    // to false and the UI hides "Load older". The page is considered final
    // when it returned strictly fewer rows than we asked for — empty page,
    // partial page, or short page after server-side thinning all qualify.
    getNextPageParam: (lastPage) =>
      lastPage.length < PAGE_LIMIT ? undefined : lastPage[lastPage.length - 1].createdAt,
  });
}

export function useVersionDetail(flavorId: string, formulaId: number, versionId: number | null) {
  return useQuery({
    queryKey: versionDetailKey(flavorId, formulaId, versionId ?? 0),
    queryFn: () => getVersionDetail(flavorId, formulaId, versionId!),
    enabled: versionId != null,
    staleTime: 60_000,
  });
}

export function useRestoreVersion(flavorId: string, formulaId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: number) => restoreVersion(flavorId, formulaId, versionId),
    onSuccess: (newDetail) => {
      // Refresh the History list so the new manual entry appears.
      qc.invalidateQueries({ queryKey: versionsKey(flavorId, formulaId) });
      // Seed the current-formulation cache from the mutation response — the
      // Export popover and any other useCurrentFormulation subscriber pick
      // up the restored items without a follow-up GET /current round-trip.
      qc.setQueryData(currentFormulationQueryKey(flavorId, formulaId), newDetail);
    },
    retry: 0,
  });
}
